<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue';

type ListItem = { text?: string } | string;
type SliderImage = { url: string; alt?: string; bg?: string };

const props = withDefaults(
  defineProps<{
    badgeText?: string;
    badgeBg?: string | null;
    titleHtml?: string;
    introHtml?: string;
    subheadingHtml?: string;
    bodyHtml?: string;
    listItems?: ListItem[] | string;
    ctaHref?: string;
    ctaTarget?: string;
    ctaText?: string;
    tipHtml?: string;
    sideImageUrl?: string;
    sideImageAlt?: string;
    sideImageBg?: string | null;
    sliderImages?: SliderImage[] | string;
  }>(),
  {
    badgeText: '',
    badgeBg: '',
    titleHtml: '',
    introHtml: '',
    subheadingHtml: '',
    bodyHtml: '',
    listItems: () => [],
    ctaHref: '',
    ctaTarget: '_self',
    ctaText: '',
    tipHtml: '',
    sideImageUrl: '',
    sideImageAlt: '',
    sideImageBg: '',
    sliderImages: () => [],
  },
);

function parseListItems(raw: ListItem[] | string | undefined): string[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        return (item?.text ?? '').trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      if (!Array.isArray(parsed)) return [];
      return (parsed as ListItem[])
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          return (item?.text ?? '').trim();
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }
  return [];
}

function parseSliderImages(raw: SliderImage[] | string | undefined): SliderImage[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((img) => img.url);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as SliderImage[];
      return Array.isArray(parsed) ? parsed.filter((img) => img.url) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const list = computed(() => parseListItems(props.listItems));
const slides = computed(() => parseSliderImages(props.sliderImages));
const hasSlider = computed(() => slides.value.length >= 2);

const badgeStyle = computed(() => {
  const bg = props.badgeBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const sideImagePanelStyle = computed(() => {
  const bg = props.sideImageBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const hasSideImage = computed(() => hasSlider.value || (props.sideImageUrl ?? '').trim().length > 0);

const show = computed(() => {
  const t = (props.titleHtml ?? '').replace(/<[^>]*>/g, '').trim();
  return (
    t.length > 0
    || list.value.length > 0
    || (props.ctaHref ?? '').trim().length > 0
  );
});

// ── Before/After Slider Logic ──
const sliderPosition = ref(50);
const isDragging = ref(false);

function getPosition(e: PointerEvent) {
  const container = (e.currentTarget as HTMLElement);
  if (!container) return 50;
  const rect = container.getBoundingClientRect();
  const x = e.clientX - rect.left;
  return Math.max(0, Math.min(100, (x / rect.width) * 100));
}

function onPointerDown(e: PointerEvent) {
  isDragging.value = true;
  sliderPosition.value = getPosition(e);
  (e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId);
}

function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return;
  sliderPosition.value = getPosition(e);
}

function onPointerUp() {
  isDragging.value = false;
}

onBeforeUnmount(() => {
  isDragging.value = false;
});
</script>

<template>
  <section
    v-if="show"
    class="pricing-cta2 mb-12 mt-24 font-inter md:mb-16 md:mt-28 md:pt-16 lg:my-80 lg:pt-0"
  >
    <div class="container">
      <div class="grid gap-20 md:gap-24 lg:grid-cols-2 lg:items-center lg:gap-48">
        <div class="px-8 text-off-black-500 md:px-0 lg:px-20">
          <div class="flex flex-col">
            <h2
              v-if="titleHtml"
              class="lg:text-34 font-inter-tight text-30 font-semibold leading-[1.35] tracking-[-0.01em] lg:max-w-[340px] lg:leading-[1.15] [&_p]:m-0 [&_p]:leading-[inherit]"
              v-html="titleHtml"
            />

            <div
              v-if="introHtml"
              class="my-8 !font-inter-tight text-20 font-semibold leading-[1.35] text-off-black-500"
              v-html="introHtml"
            />

            <!-- Mobile image / slider -->
            <div
              v-if="hasSideImage"
              class="relative my-32 w-full lg:hidden"
            >
              <!-- Before/After slider (mobile) -->
              <div
                v-if="hasSlider"
                class="ba-slider relative w-full overflow-hidden rounded-[16px] pt-[100%] sm:pt-[80%] md:pt-[110%]"
                :class="{ 'cursor-grabbing': isDragging, 'cursor-grab': !isDragging }"
                @pointerdown="onPointerDown"
                @pointermove="onPointerMove"
                @pointerup="onPointerUp"
              >
                <!-- After (bottom layer, full) -->
                <div
                  class="absolute inset-0"
                  :style="{ backgroundColor: slides[1]?.bg || '#e8e3de' }"
                >
                  <img
                    :src="slides[1].url"
                    :alt="slides[1].alt || 'After'"
                    class="size-full object-cover"
                    loading="lazy"
                    decoding="async"
                  >
                </div>

                <!-- Before (top layer, clipped) -->
                <div
                  class="absolute inset-0"
                  :style="{
                    backgroundColor: slides[0]?.bg || '#ddd6cb',
                    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                  }"
                >
                  <img
                    :src="slides[0].url"
                    :alt="slides[0].alt || 'Before'"
                    class="size-full object-cover"
                    loading="lazy"
                    decoding="async"
                  >
                </div>

                <!-- Divider -->
                <div
                  class="absolute inset-y-0 z-20 w-[1.5px] -translate-x-1/2 bg-white"
                  :style="{ left: sliderPosition + '%' }"
                >
                  <div class="absolute left-1/2 top-1/2 flex size-40 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-white bg-white shadow-md">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      class="text-off-black-500"
                    >
                      <path
                        d="M8 5L3 12L8 19"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M16 5L21 12L16 19"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                <!-- Badge: bottom-left on small screens (this block is lg:hidden) -->
                <span
                  v-if="badgeText"
                  class="absolute bottom-12 left-12 top-auto z-30 inline-block rounded-full px-12 py-4 text-11 font-semibold !text-white sm:bottom-16 sm:left-16 sm:px-14 sm:py-5 sm:text-12"
                  :style="badgeStyle"
                >{{ badgeText }}</span>
              </div>

              <!-- Single image fallback (mobile) -->
              <div
                v-else
                class="relative w-full overflow-hidden rounded-[16px] pt-[100%] sm:pt-[80%] md:pt-[110%]"
                :style="sideImagePanelStyle"
              >
                <span
                  v-if="badgeText"
                  class="absolute bottom-12 left-12 top-auto z-30 inline-block rounded-full px-12 py-4 text-11 font-semibold !text-white sm:bottom-16 sm:left-16 sm:px-14 sm:py-5 sm:text-12"
                  :style="badgeStyle"
                >{{ badgeText }}</span>
                <img
                  :src="sideImageUrl"
                  :alt="sideImageAlt || ''"
                  class="absolute inset-0 size-full rounded-[16px] object-cover"
                  loading="lazy"
                  decoding="async"
                >
              </div>
            </div>

            <div class="max-w-[720px] lg:mt-48">
              <ul
                v-if="list.length"
                class="space-y-20 font-inter text-14 font-normal leading-[1.3] lg:space-y-24 lg:text-16"
              >
                <li
                  v-for="(item, index) in list"
                  :key="`${index}-${item}`"
                  class="flex items-start gap-8"
                >
                  <span class="-mt-1 shrink-0 text-copper-500">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="size-24"
                    >
                      <path
                        d="M15.5 10L10.8333 15L8.5 12.9339M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                        stroke="#AC6D3F"
                        stroke-width="1.75"
                        stroke-linecap="round"
                      />
                    </svg>
                  </span>
                  <span>{{ item }}</span>
                </li>
              </ul>

              <h3
                v-if="subheadingHtml"
                class="mt-16 font-inter text-[15.5px] font-semibold text-off-black-500 md:text-20 lg:mt-20"
                v-html="subheadingHtml"
              />

              <div
                v-if="bodyHtml"
                class="cta2-body my-24 max-w-[600px] text-10 leading-[1.5] text-off-black-500/80 md:text-10"
                v-html="bodyHtml"
              />

              <div
                v-if="ctaHref"
                class="mt-20 md:mt-24 lg:mt-28"
              >
                <a
                  :href="ctaHref"
                  :target="ctaTarget || '_self'"
                  class="mx-auto flex w-full max-w-full items-center justify-center rounded-full bg-off-black-500 px-28 py-14 font-inter-tight text-14 font-semibold tracking-wide text-white transition-opacity hover:opacity-90 lg:mx-0 lg:px-32 lg:py-16 lg:text-16 xl:w-fit"
                >
                  {{ ctaText }}
                </a>
              </div>

              <p
                v-if="tipHtml"
                :class="[
                  'pricing-cta2-tip text-off-black-50 mx-auto max-w-[min(100%,420px)] text-center font-inter text-10 font-normal leading-[1.35] lg:mx-0 lg:max-w-[520px] lg:text-left',
                  ctaHref ? 'mt-6 lg:mt-4' : 'mt-12 md:mt-16 lg:mt-8',
                ]"
                v-html="tipHtml"
              />
            </div>
          </div>
        </div>

        <!-- Desktop image / slider -->
        <div
          v-if="hasSideImage"
          class="relative mx-auto hidden w-full max-w-[530px] md:px-0 lg:mx-0 lg:block lg:justify-self-end"
        >
          <!-- Before/After slider (desktop) -->
          <div
            v-if="hasSlider"
            class="ba-slider relative h-[620px] w-full overflow-hidden rounded-[22px] lg:h-[700px] xl:h-[620px]"
            :class="{ 'cursor-grabbing': isDragging, 'cursor-grab': !isDragging }"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
          >
            <!-- After (bottom layer, full) -->
            <div
              class="absolute inset-0"
              :style="{ backgroundColor: slides[1]?.bg || '#e8e3de' }"
            >
              <img
                :src="slides[1].url"
                :alt="slides[1].alt || 'After'"
                class="size-full object-cover"
                loading="lazy"
                decoding="async"
              >
            </div>

            <!-- Before (top layer, clipped) -->
            <div
              class="absolute inset-0"
              :style="{
                backgroundColor: slides[0]?.bg || '#ddd6cb',
                clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
              }"
            >
              <img
                :src="slides[0].url"
                :alt="slides[0].alt || 'Before'"
                class="size-full object-cover"
                loading="lazy"
                decoding="async"
              >
            </div>

            <!-- Divider -->
            <div
              class="absolute inset-y-0 z-20 w-[1.5px] -translate-x-1/2 bg-white"
              :style="{ left: sliderPosition + '%' }"
            >
              <div class="absolute left-1/2 top-1/2 flex size-48 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-white bg-white shadow-md">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="text-off-black-500"
                >
                  <path
                    d="M8 5L3 12L8 19"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16 5L21 12L16 19"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>

            <!-- Badge: top-left on desktop only -->
            <span
              v-if="badgeText"
              class="absolute left-20 top-20 z-30 inline-block rounded-full px-14 py-6 text-12 font-semibold text-white"
              :style="badgeStyle"
            >{{ badgeText }}</span>
          </div>

          <!-- Single image fallback (desktop) -->
          <div
            v-else
            class="relative h-[620px] w-full overflow-hidden rounded-[22px] lg:h-[700px] xl:h-[620px]"
            :style="sideImagePanelStyle"
          >
            <span
              v-if="badgeText"
              class="absolute left-20 top-20 z-30 inline-block rounded-full px-14 py-6 text-12 font-semibold text-white"
              :style="badgeStyle"
            >{{ badgeText }}</span>
            <img
              :src="sideImageUrl"
              :alt="sideImageAlt || ''"
              class="absolute inset-0 size-full rounded-[22px] object-cover"
              loading="lazy"
              decoding="async"
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.cta2-body :deep(strong) {
  font-weight: 700;
  color: inherit;
}

.cta2-body :deep(p + p) {
  margin-top: 8px;
}

.ba-slider {
  touch-action: none;
  user-select: none;
}
</style>
